// src/components/UserProjectsPage.tsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Header,
  Content,
  Progress,
  Table,
} from '@backstage/core-components';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { Button, Box, Typography, Chip, makeStyles } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import yaml from 'js-yaml';

const useStyles = makeStyles(theme => ({
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
}));

export const ProjectsPage = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [allProjects, setAllProjects] = useState<string[]>([]);
  const [accessible, setAccessible] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const fetchLists = useCallback(async (force = false) => {
    setLoading(true);
    setError(undefined);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      // 1) fetch /backstage/projects
      const paramsRes = await fetch(`${baseUrl}/aws/params`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: '/backstage/projects' }),
      });
      // Check the status before reading `value`. Without this, a backend failure
      // (missing AWS credentials, no SSM access, 401) yields a body with no `value`,
      // and the CSV fallback below then crashes on `undefined.split(',')` — which
      // reported "Cannot read properties of undefined (reading 'split')" and hid the
      // actual cause.
      if (!paramsRes.ok) {
        const body = await paramsRes.text().catch(() => '');
        throw new Error(
          `Failed to load /backstage/projects from ${baseUrl}/aws/params ` +
            `(HTTP ${paramsRes.status})${body ? `: ${body.slice(0, 300)}` : ''}`,
        );
      }
      const p = (await paramsRes.json()).value as string | undefined;
      if (typeof p !== 'string') {
        throw new Error(
          '/aws/params returned no "value" for /backstage/projects — the SSM parameter ' +
            'may be missing or the backend could not reach AWS.',
        );
      }

      let map: Record<string, any>;
      try {
        const parsed = yaml.load(p);
        map = (parsed && typeof parsed === 'object')
          ? parsed as any
          : {};
      } catch {
        map = {};
      }
      // fallback CSV keys
      if (!Object.keys(map).length) {
        p.split(',').map(s => s.trim()).filter(Boolean).forEach(k => map[k] = {});
      }
      setAllProjects(Object.keys(map));

      // 2) fetch user-accessible list
      const userRes = await fetch(`${baseUrl}/ado/user/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username: email, forceRefresh: force }),
      });
      if (!userRes.ok) {
        const body = await userRes.text().catch(() => '');
        throw new Error(
          `Failed to load your accessible projects (HTTP ${userRes.status})` +
            `${body ? `: ${body.slice(0, 300)}` : ''}`,
        );
      }
      const u = (await userRes.json()).projects as string[] | undefined;
      setAccessible(Array.isArray(u) ? u : []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, identityApi, email]);

  useEffect(() => {
    identityApi.getProfileInfo()
      .then(p => p.email && setEmail(p.email))
      .catch(console.error);
  }, [identityApi]);

  useEffect(() => {
    if (email) fetchLists();
  }, [email, fetchLists]);

  const handleRefresh = () => fetchLists(true);
  const others = allProjects.filter(p => !accessible.includes(p));

  return (
    <Page themeId="home">
      <Header title="Your Projects">
        <Button variant="contained" color="primary" onClick={handleRefresh}>
          Refresh
        </Button>
      </Header>
      <Content>
        {error && (
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
        )}
        {loading ? (
          <Progress />
        ) : (
          <>
            <Table<{ name: string }>
              title="Projects You Have Access To"
              options={{ paging: false, search: false }}
              columns={[{ title: 'Project', field: 'name' }]}
              data={accessible.map(n => ({ name: n }))}
              onRowClick={(_, row) => row && navigate(`/projects/${row.name}`)}
            />

            <Box mt={3}>
              <Typography variant="h6">Other Projects</Typography>
              <Box className={classes.chipContainer}>
                {others.map(name => (
                  <Chip
                    key={name}
                    label={name}
                    variant="outlined"
                    color="default"
                  />
                ))}
              </Box>
            </Box>
          </>
        )}
      </Content>
    </Page>
  );
};
